<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import LabelItem from 'dashboard/components-next/Label/LabelItem.vue';
import AddLabel from 'dashboard/components-next/Label/AddLabel.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const hoveredLabel = ref(null);

const allLabels = useMapGetter('labels/getLabels');

const localContactLabels = ref([]);

const savedLabels = computed(() => {
  const lowerCaseLabels = localContactLabels.value.map(l => l.toLowerCase());
  return allLabels.value.filter(({ title }) =>
    lowerCaseLabels.includes(title.toLowerCase())
  );
});

const labelMenuItems = computed(() => {
  return allLabels.value
    ?.map(label => ({
      label: label.title,
      value: label.id,
      thumbnail: { name: label.title, color: label.color },
      isSelected: savedLabels.value.some(
        savedLabel => savedLabel.id === label.id
      ),
      action: 'contactLabel',
    }))
    .toSorted((a, b) => Number(a.isSelected) - Number(b.isSelected));
});

const fetchLabels = async id => {
  if (!id) {
    return;
  }
  await store.dispatch('contactLabels/get', id);
  const storeLabels = store.getters['contactLabels/getContactLabels'];
  localContactLabels.value = storeLabels(id) || [];
};

const handleLabelAction = async ({ value }) => {
  try {
    const currentLabels = savedLabels.value.map(label => label.title);

    const selectedLabel = allLabels.value.find(label => label.id === value);
    if (!selectedLabel) return;

    let updatedLabels;
    const lowerTitle = selectedLabel.title.toLowerCase();

    if (currentLabels.some(l => l.toLowerCase() === lowerTitle)) {
      updatedLabels = currentLabels.filter(
        labelTitle => labelTitle.toLowerCase() !== lowerTitle
      );
    } else {
      updatedLabels = [...currentLabels, selectedLabel.title];
    }

    localContactLabels.value = updatedLabels;

    await store.dispatch('contactLabels/update', {
      contactId: props.contactId,
      labels: updatedLabels,
    });

    await fetchLabels(props.contactId);
  } catch {
    await fetchLabels(props.contactId);
    useAlert(t('CONTACT_PANEL.LABELS.CONTACT.ERROR'));
  }
};

const handleRemoveLabel = label => {
  return handleLabelAction({ value: label.id });
};

watch(
  () => props.contactId,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      fetchLabels(newVal);
    }
  }
);

onMounted(() => {
  const id = props.contactId || route.params.contactId;
  if (id) {
    fetchLabels(id);
  }
});

const handleMouseLeave = () => {
  hoveredLabel.value = null;
};

const handleLabelHover = labelId => {
  hoveredLabel.value = labelId;
};
</script>

<template>
  <div class="flex flex-wrap items-center gap-2" @mouseleave="handleMouseLeave">
    <LabelItem
      v-for="label in savedLabels"
      :key="label.id"
      :label="label"
      :is-hovered="hoveredLabel === label.id"
      @remove="handleRemoveLabel"
      @hover="handleLabelHover(label.id)"
    />
    <AddLabel
      :label-menu-items="labelMenuItems"
      @update-label="handleLabelAction"
    />
  </div>
</template>
